/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks';
import { notificationActions } from '../../redux/actions';
import { studentService } from '../../services';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, rgba(0, 150, 255, 0.4) 0%, rgba(100, 200, 255, 0.3) 100%);
  padding: 20px 0;
`;

const PageContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  gap: 12px;

  h1 {
    color: white;
    margin: 0;
    font-size: 28px;
    font-weight: 700;
  }
`;

const BackButton = styled.button`
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ExamCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  margin-bottom: 20px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;

  h2 {
    margin: 0;
    color: #0f172a;
    font-size: 20px;
    font-weight: 700;
    flex: 1;
    text-align: right;
  }
`;

const BackButtonInCard = styled.button`
  padding: 10px 16px;
  background: linear-gradient(90deg, #007bff 0%, #00bfff 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 16px;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(0, 127, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const ExamInfo = styled.div`
  margin-bottom: 20px;

  h2 {
    margin: 0 0 8px 0;
    color: #0f172a;
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 14px;
  }
`;

const QuestionContainer = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

const QuestionText = styled.div`
  background: #f1f5f9;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-weight: 500;
  color: #0f172a;
  line-height: 1.6;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    background: #f8f9ff;
  }

  input[type='radio'] {
    margin-right: 8px;
    cursor: pointer;
  }

  &.selected {
    border-color: #667eea;
    background: #f0f4ff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #d0d7e2;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const SubText = styled.div`
  color: #475569;
  font-size: 14px;
  margin-bottom: 8px;
`;

const WordBox = styled.div<{ selected?: boolean; disabled?: boolean }>`
  display: inline-block;
  padding: 6px 12px;
  margin: 0;
  background: ${p => p.selected ? '#e0e7ff' : '#ffffff'};
  border: 2px solid ${p => p.selected ? '#6366f1' : '#cbd5e1'};
  border-radius: 10px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  font-size: 15px;
  color: ${p => p.selected ? '#4338ca' : '#0f172a'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${p => p.selected ? '0 4px 12px rgba(99, 102, 241, 0.25)' : '0 2px 6px rgba(0, 0, 0, 0.08)'};
  user-select: none;
  opacity: ${p => p.disabled ? 0.4 : 1};
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    ${p => !p.disabled && `
      border-color: #6366f1;
      box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
      transform: translateY(-2px) scale(1.05);
    `}
  }
  
  &:active {
    ${p => !p.disabled && `
      transform: scale(0.98);
    `}
  }
`;

const WordBoxesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  min-height: 60px;
  margin-bottom: 16px;
`;

const AnswerArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 12px;
  background: #fefce8;
  border: 2px dashed #fbbf24;
  border-radius: 12px;
  min-height: 60px;
  margin-bottom: 12px;
  position: relative;
  align-items: flex-start;
  
  &:empty:before {
    content: 'Ấn vào từ để sắp xếp, ấn lần nữa để đổi lại...';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: #94a3b8;
    font-style: italic;
    pointer-events: none;
  }
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PreviewText = styled.span`
  font-size: 15px;
  font-weight: 700;
  font-style: italic;
  color: #3b82f6;
  text-transform: none;
  letter-spacing: normal;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px 20px;

  .spinner {
    border: 4px solid #e2e8f0;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ExerciseHeader = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 12px 20px;
  border-radius: 8px;
  margin: 24px 0 16px 0;
  border-left: 4px solid #667eea;
`;

const ResultOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 32px 12px;
  z-index: 2000;
  overflow-y: auto;
`;

const ResultCard = styled.div`
  width: min(960px, 100%);
  background: white;
  border-radius: 14px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.18);
  padding: 24px;
  border: 1px solid #e2e8f0;
`;

const AnswerDetail = styled.div<{ $isCorrect: boolean }>`
  border: 1px solid ${p => p.$isCorrect ? '#bbf7d0' : '#fecdd3'};
  background: ${p => p.$isCorrect ? '#f0fdf4' : '#fff1f2'};
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
`;

// ============ Helper functions for Type 12 (True/False) ============
// Normalize True/False values to single-letter tokens for consistent comparison
const normalizeTF = (val: any) => {
  const v = String(val ?? '').trim().toLowerCase();
  if (v === 't' || v === 'true') return 'T';
  if (v === 'f' || v === 'false') return 'F';
  return v ? v[0].toUpperCase() : '';
};

const formatTFLabel = (norm: string) => {
  if (norm === 'T') return 'True';
  if (norm === 'F') return 'False';
  return norm || '—';
};

const parseTFAnswerParts = (answer: any, expectedLength?: number) => {
  const parts = Array.isArray(answer)
    ? answer
    : String(answer ?? '')
        .split(' | ')
        .map((p: string) => p.trim())
        .filter((p) => p.length > 0);
  const normalized = parts.map((p) => normalizeTF(p));
  if (expectedLength && normalized.length < expectedLength) {
    return [...normalized, ...Array(expectedLength - normalized.length).fill('')];
  }
  return normalized;
};

// Normalize answer string for comparison (same logic as backend and PublicExamPage)
const normalizeAnswer = (value: string): string => {
  if (typeof value !== 'string') return value;
  
  let normalized = value.trim();
  // Normalize smart quotes to ASCII
  normalized = normalized.replace(/['']/g, "'");
  normalized = normalized.replace(/[""]/g, '"');
  // Remove trailing period(s) and spaces: "abc ." → "abc"
  normalized = normalized.replace(/\s*\.+\s*$/g, '');
  // Normalize spaces around punctuation: "a,b" | "a , b" → "a,b"
  normalized = normalized.replace(/\s*,\s*/g, ',');
  normalized = normalized.replace(/\s*\?\s*/g, '?');
  // Collapse multiple spaces to single space
  normalized = normalized.replace(/\s+/g, ' ');
  return normalized.toLowerCase();
};

interface RetryExamPageProps {}

const RetryExamPage: React.FC<RetryExamPageProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [exam, setExam] = useState<any>(null);
  const [answers, setAnswers] = useState<{ [key: string]: any }>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [submittedAnswers, setSubmittedAnswers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadExam = async () => {
      try {
        console.log('🔵 [RetryExamPage] useEffect started');
        setLoading(true);
        
        console.log('🔵 [RetryExamPage] Fetching exam...');
        const examData = await studentService.getRetryExamQuestions();
        console.log('🔵 [RetryExamPage] Received:', examData);
        
        if (!examData || !examData.exam_questions) {
          throw new Error('Invalid exam data');
        }
        
        setExam(examData);
        setLoading(false);
        console.log('✅ [RetryExamPage] Loaded successfully');
      } catch (error: any) {
        console.error('🔴 [RetryExamPage] Error:', error);
        const errorMessage = typeof error === 'string' ? error : error?.message || 'Không tìm thấy bài luyện tập';
        dispatch(notificationActions.addNotification(errorMessage, 'DANGER'));
        setLoading(false);
        setTimeout(() => navigate('/student'), 1500);
      }
    };

    loadExam();
  }, []);

  // Group questions by type (same as PublicExamPage)
  const groupedByType = useMemo(() => {
    if (!exam?.exam_questions) return [];
    const grouped: Record<number, { typeId: number; typeInfo: any; questions: any[] }> = {};
    exam.exam_questions.forEach((eq: any) => {
      const typeId = eq.question?.question_type_id || 0;
      if (!grouped[typeId]) {
        grouped[typeId] = { typeId, typeInfo: eq.question?.questionType, questions: [] };
      }
      grouped[typeId].questions.push(eq);
    });
    return Object.values(grouped).sort((a, b) => a.typeId - b.typeId);
  }, [exam]);

  // Build question number map for continuous numbering
  const questionNumberMap = useMemo(() => {
    const m: Record<number, number> = {};
    let counter = 1;
    groupedByType.forEach((g) => {
      g.questions.forEach((eq) => {
        if (eq.question?.id) {
          m[eq.question.id] = counter;
          counter += 1;
        }
      });
    });
    return m;
  }, [groupedByType]);

  const renderQuestionText = (q: any) => {
    let c = q.content || {};
    if (typeof c === 'string') {
      try {
        c = JSON.parse(c);
      } catch {
        c = {};
      }
    }
    const typeId = q.question_type_id;
    
    // Type 10: only show sentence
    if (typeId === 10) return c.sentence || '';

    // Type 1, 2, 5, 8, 12, 13: plain text
    if (c.text) return c.text;
    if (c.original_sentence) return c.original_sentence;
    if (c.sentence) return c.sentence;
    
    // Type 3: Sentence Ordering - show words
    if (typeId === 3 && c.words) return c.words.join(' / ');
    
    // Type 4: Write Sentence - show first prompt
    if (typeId === 4 && c.prompts && c.prompts.length > 0) return c.prompts[0].text;
    
    // Type 6, 7, 9: Reading/Fill Blanks - show passage
    if ((typeId === 6 || typeId === 7 || typeId === 9) && c.passage) return c.passage;

    // Type 12: True/False - show passage and statements
    if (typeId === 12) {
      const passageBlock = c.passage ? `${c.passage}\n` : '';
      if (Array.isArray(c.statements) && c.statements.length > 0) {
        const stmtText = c.statements.map((s: any, idx: number) => `${idx + 1}) ${s?.text || ''}`).join(' | ');
        return `${passageBlock}${stmtText}`.trim();
      }
      return c.passage || c.text || '';
    }
    
    return '';
  };

  const renderQuestionBody = (q: any) => {
    let c = q.content || {};
    if (typeof c === 'string') {
      try {
        c = JSON.parse(c);
      } catch {
        c = {};
      }
    }
    const typeId = q.question_type_id;
    const questionId = q.id; // Use question.id directly

    // Type 1: Multiple choice
    if (typeId === 1 && c.options) {
      return (
        <OptionsContainer>
          {c.options.map((opt: string, i: number) => (
            <OptionLabel
              key={i}
              className={answers[questionId] === opt ? 'selected' : ''}
            >
              <input
                type="radio"
                name={`q_${questionId}`}
                value={opt}
                checked={answers[questionId] === opt}
                onChange={(e) => setAnswers(prev => ({ ...prev, [questionId]: e.target.value }))}
              />
              <span><strong>{String.fromCharCode(65 + i)}.</strong> {opt}</span>
            </OptionLabel>
          ))}
        </OptionsContainer>
      );
    }

    // Type 3: Sentence Ordering - interactive word boxes
    if (typeId === 3 && c.words) {
      const selectedWords = answers[questionId] || [];
      const isWordSelected = (index: number) => selectedWords.includes(index);
      
      const handleWordClick = (index: number) => {
        setAnswers((prev) => {
          const current = prev[questionId] || [];
          if (current.includes(index)) {
            return { ...prev, [questionId]: current.filter((i: number) => i !== index) };
          } else {
            return { ...prev, [questionId]: [...current, index] };
          }
        });
      };
      
      const sentenceText = selectedWords.map((idx: number) => c.words[idx]).join(' ');
      
      return (
        <div>
          <SectionLabel>Các từ cần sắp xếp:</SectionLabel>
          <WordBoxesContainer>
            {c.words.map((word: string, idx: number) => (
              <WordBox
                key={idx}
                selected={isWordSelected(idx)}
                disabled={isWordSelected(idx)}
                onClick={() => !isWordSelected(idx) && handleWordClick(idx)}
              >
                {word}
              </WordBox>
            ))}
          </WordBoxesContainer>
          
          <SectionLabel>
            Câu trả lời của bạn:
            {sentenceText && <PreviewText>{sentenceText}</PreviewText>}
          </SectionLabel>
          <AnswerArea>
            {selectedWords.map((idx: number, position: number) => (
              <WordBox
                key={`selected-${idx}-${position}`}
                selected={true}
                onClick={() => handleWordClick(idx)}
              >
                {c.words[idx]}
              </WordBox>
            ))}
          </AnswerArea>
        </div>
      );
    }

    // Type 4: Write Sentence - prompts
    if (typeId === 4 && c.prompts) {
      const promptAnswers = Array.isArray(answers[questionId]) ? answers[questionId] : [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {c.prompts.map((p: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <SubText style={{ fontWeight: 600, color: '#333' }}>Câu {idx + 1}: {p.text}</SubText>
              <TextArea
                placeholder={`Trả lời câu ${idx + 1}...`}
                value={promptAnswers[idx] || ''}
                onChange={(e) => {
                  const newAnswers = [...promptAnswers];
                  newAnswers[idx] = e.target.value;
                  setAnswers(prev => ({ ...prev, [questionId]: newAnswers }));
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    // Type 5: Rewrite sentence
    if (typeId === 5 && c.original_sentence) {
      return (
        <>
          <SubText>{c.original_sentence}</SubText>
          {c.instruction && <SubText style={{ fontStyle: 'italic' }}>→ {c.instruction}</SubText>}
          <TextArea
            placeholder="Viết lại câu..."
            value={answers[questionId] || ''}
            onChange={(e) => setAnswers(prev => ({ ...prev, [questionId]: e.target.value }))}
          />
        </>
      );
    }

    // Type 6: Reading Essay - passage + questions
    if (typeId === 6 && c.passage) {
      return (
        <>
          <SubText style={{ fontStyle: 'italic', color: '#555', marginBottom: '12px' }}>
            {c.passage}
          </SubText>
          {c.questions && c.questions.map((subQ: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                Câu {idx + 1}: {subQ.question_text}
              </div>
              <TextArea
                placeholder={`Trả lời câu hỏi ${idx + 1}...`}
                value={answers[subQ.id] || ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [subQ.id]: e.target.value }))}
                style={{ minHeight: '80px' }}
              />
            </div>
          ))}
        </>
      );
    }

    // Type 7: Reading MCQ - passage + MCQ questions
    if (typeId === 7 && c.passage) {
      return (
        <>
          <SubText style={{ fontStyle: 'italic', color: '#555', marginBottom: '12px' }}>
            {c.passage}
          </SubText>
          {c.questions && c.questions.map((subQ: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                Câu {idx + 1}: {subQ.question_text}
              </div>
              <OptionsContainer>
                {subQ.options && subQ.options.map((opt: string, optIdx: number) => {
                  const optionLabel = String.fromCharCode(65 + optIdx);
                  return (
                    <OptionLabel key={optIdx} className={answers[subQ.id] === opt ? 'selected' : ''}>
                      <input
                        type="radio"
                        name={`q_${subQ.id}`}
                        checked={answers[subQ.id] === opt}
                        onChange={() => setAnswers((prev) => ({ ...prev, [subQ.id]: opt }))}
                      />
                      <span><strong>{optionLabel}.</strong> {opt}</span>
                    </OptionLabel>
                  );
                })}
              </OptionsContainer>
            </div>
          ))}
        </>
      );
    }

    // Type 9: Fill Blanks MCQ
    if (typeId === 9 && c.passage && Array.isArray(c.questions)) {
      const blankRegex = /_{3,}|\.{3,}/g;
      const blanksInText = (c.passage.match(blankRegex) || []).length;
      const blanksCount = Math.max(blanksInText, c.questions.length);
      
      let idx = 1;
      const indexedPassage = c.passage.replace(blankRegex, () => {
        if (idx > blanksCount) idx = blanksCount;
        const text = `(${idx}) ____`;
        idx += 1;
        return text;
      });
      
      const currentAnswers = Array.isArray(answers[questionId]) ? answers[questionId] : Array(blanksCount).fill('');

      return (
        <>
          <SubText style={{ fontStyle: 'italic', color: '#555', marginBottom: '12px', lineHeight: 1.7 }}>
            {indexedPassage}
          </SubText>
          {c.questions.map((subQ: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                Chỗ trống {idx + 1}
              </div>
              <OptionsContainer>
                {(subQ.options || []).map((opt: string, optIdx: number) => (
                  <OptionLabel
                    key={optIdx}
                    className={currentAnswers[idx] === opt ? 'selected' : ''}
                  >
                    <input
                      type="radio"
                      name={`${questionId}_blank_${idx}`}
                      checked={currentAnswers[idx] === opt}
                      onChange={() => {
                        const newAnswers = [...currentAnswers];
                        newAnswers[idx] = opt;
                        setAnswers(prev => ({ ...prev, [questionId]: newAnswers }));
                      }}
                    />
                    <span><strong>{String.fromCharCode(65 + optIdx)}.</strong> {opt}</span>
                  </OptionLabel>
                ))}
              </OptionsContainer>
            </div>
          ))}
        </>
      );
    }

    // Type 8: Fill Blanks Word Bank
    if (typeId === 8 && c.text && c.word_bank) {
      const blankRegex = /_{3,}|\.{3,}/g;
      const parts = c.text.split(blankRegex);
      const blanksCount = (c.text.match(blankRegex) || []).length;
      const currentAnswers = Array.isArray(answers[questionId]) ? answers[questionId] : Array(blanksCount).fill('');
      
      return (
        <>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px', 
            marginBottom: '16px',
            padding: '12px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px dashed #dee2e6'
          }}>
            <strong style={{ width: '100%', marginBottom: '4px', color: '#495057' }}>Word Bank:</strong>
            {c.word_bank.map((word: string, idx: number) => (
              <span
                key={idx}
                style={{
                  padding: '6px 12px',
                  background: 'white',
                  border: '2px solid #007bff',
                  borderRadius: '6px',
                  fontWeight: 600,
                  color: '#007bff',
                  fontSize: '14px'
                }}
              >
                {word}
              </span>
            ))}
          </div>
          
          <div style={{ lineHeight: '2', fontSize: '15px' }}>
            {parts.map((part: string, idx: number) => (
              <span key={idx}>
                {part}
                {idx < blanksCount && (
                  <select
                    value={currentAnswers[idx] || ''}
                    onChange={(e) => {
                      const newAnswers = [...currentAnswers];
                      newAnswers[idx] = e.target.value;
                      setAnswers(prev => ({ ...prev, [questionId]: newAnswers }));
                    }}
                    style={{
                      padding: '4px 8px',
                      margin: '0 4px',
                      border: '2px solid #007bff',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: 'pointer',
                      fontWeight: 600,
                      minWidth: '100px'
                    }}
                  >
                    <option value="">---</option>
                    {c.word_bank.map((word: string, wIdx: number) => (
                      <option key={wIdx} value={word}>{word}</option>
                    ))}
                  </select>
                )}
              </span>
            ))}
          </div>
        </>
      );
    }

    // Type 10: Error Identification - only options (sentence shown in title)
    if (typeId === 10 && c.sentence && c.options) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {c.options.map((opt: string, i: number) => (
            <OptionLabel
              key={i}
              className={answers[questionId] === String(i) ? 'selected' : ''}
            >
              <input
                type="radio"
                name={`q_${questionId}`}
                checked={answers[questionId] === String(i)}
                onChange={() => setAnswers(prev => ({ ...prev, [questionId]: String(i) }))}
              />
              <span><strong>{String.fromCharCode(65 + i)}.</strong> {opt}</span>
            </OptionLabel>
          ))}
        </div>
      );
    }

    // Type 12: True/False - passage + multiple statements
    if (typeId === 12 && Array.isArray(c.statements)) {
      const passage = c.passage || '';
      const currentAnswers = Array.isArray(answers[questionId])
        ? (answers[questionId] as string[]).map((val) => normalizeTF(val))
        : Array(c.statements.length).fill('');
      return (
        <>
          {passage && (
            <SubText style={{ fontStyle: 'italic', color: '#555', marginBottom: '12px' }}>
              {passage} {questionId && <span style={{ fontStyle: 'italic', fontSize: 12, color: '#666' }}>[{questionId}]</span>}
            </SubText>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {c.statements.map((stmt: any, idx: number) => (
              <div key={idx} style={{ padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff' }}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>
                  Câu {idx + 1}: {stmt.text}
                  {!passage && questionId && idx === 0 && (
                    <span style={{ fontStyle: 'italic', fontSize: 12, color: '#666' }}> [{questionId}]</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  {[
                    { value: 'T', label: '✓ Đúng (True)' },
                    { value: 'F', label: '✗ Sai (False)' },
                  ].map(({ value, label }) => (
                    <OptionLabel key={value} style={{ marginBottom: 0, flex: 1 }}>
                      <input
                        type="radio"
                        name={`q_${questionId}_stmt_${idx}`}
                        checked={currentAnswers[idx] === value}
                        onChange={() => {
                          const next = [...currentAnswers];
                          next[idx] = value;
                          setAnswers((prev) => ({ ...prev, [questionId]: next }));
                        }}
                      />
                      <span>{label}</span>
                    </OptionLabel>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    // Type 11: Make Question - show sentence with underline, text input for question
    if (typeId === 11 && c.sentence) {
      const sentence = c.sentence;
      const underlined = c.underlined || '';
      return (
        <>
          <SubText style={{ fontWeight: 500, marginBottom: '12px', lineHeight: 1.6 }}>
            {underlined && sentence.includes(underlined) ? (
              <>
                {sentence.split(underlined)[0]}
                <u style={{ fontWeight: 'bold', textDecoration: 'underline' }}>{underlined}</u>
                {sentence.split(underlined)[1] || ''}
              </>
            ) : (
              sentence
            )}
          </SubText>
          <TextArea
            placeholder="Đặt câu hỏi cho phần gạch chân..."
            value={answers[questionId] || ''}
            onChange={(e) => setAnswers(prev => ({ ...prev, [questionId]: e.target.value }))}
          />
        </>
      );
    }

    // Type 2, 10, 11, 12, 13 and fallback: show textarea (same as PublicExamPage)
    return (
      <TextArea
        placeholder="Nhập câu trả lời..."
        value={answers[questionId] || ''}
        onChange={(e) => setAnswers(prev => ({ ...prev, [questionId]: e.target.value }))}
      />
    );
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Build question type map (like PublicExamPage)
      const questionTypeMap = new Map<number, number>();
      exam.exam_questions.forEach((eq: any) => {
        const q = eq.question;
        if (q?.id && q?.question_type_id) {
          questionTypeMap.set(Number(q.id), Number(q.question_type_id));
        }
      });

      // Format answers: include all questions, default '' when not answered (fix Type 10 empty submit)
      const formattedAnswers = exam.exam_questions.map((eq: any) => {
        const qid = eq.question?.id ? Number(eq.question.id) : null;
        if (!qid) return null;
        const typeId = questionTypeMap.get(qid);

        const answer = answers[qid] !== undefined ? answers[qid] : '';

        let finalAnswer = answer;
        if (Array.isArray(answer)) {
          if (typeId === 3) {
            // Type 3 (Sentence Ordering): answer is array of word indices
            const question = exam.exam_questions.find((eq: any) => eq.question?.id === qid);
            if (question?.question?.content?.words) {
              const words = question.question.content.words;
              finalAnswer = answer.map((idx: number) => words[idx]).join(' ');
            } else {
              finalAnswer = '';
            }
          } else if (typeId === 4) {
            finalAnswer = answer.join(' | ');
          } else if (typeId === 8) {
            finalAnswer = answer.join(', ');
          } else if (typeId === 9) {
            finalAnswer = answer.join(' | ');
          } else if (typeId === 12) {
            // Type 12: join T/F values with ' | '
            finalAnswer = answer.join(' | ');
          }
        }

        return { question_id: qid, answer: finalAnswer };
      }).filter(Boolean);

      console.log('📤 Submitting retry exam answers:', formattedAnswers);

      // Submit retry exam
      const result = await studentService.submitRetryExam(formattedAnswers);
      console.log('✅ Retry exam result:', result);

      setSubmittedAnswers(formattedAnswers);
      setResult(result);

      dispatch(notificationActions.addNotification(
        `Kết quả: ${result.correct}/${result.total} câu đúng (${result.percentage}%)`,
        'SUCCESS'
      ));
    } catch (error: any) {
      console.error('Error submitting retry exam:', error);
      const errorMessage = typeof error === 'string' ? error : error?.message || 'Lỗi khi nộp bài';
      dispatch(notificationActions.addNotification(
        errorMessage
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const getQuestionById = (qid: number) => {
    return exam?.exam_questions?.find((eq: any) => eq.question?.id === qid)?.question || null;
  };

  const formatAnswerDisplay = (ans: any) => {
    if (ans === null || ans === undefined) return '(Trống)';
    if (Array.isArray(ans)) return ans.join(' | ');
    return String(ans);
  };


  if (loading || !exam) {
    return (
      <PageContainer>
        <PageContent>
          <LoadingSpinner>
            <div className="spinner"></div>
            <p style={{ color: 'white', marginTop: '16px' }}>Đang tải bài tập...</p>
          </LoadingSpinner>
        </PageContent>
      </PageContainer>
    );
  }

  const renderResultModal = () => {
    if (!result) return null;
    const perQuestions = result.per_question || [];

    return (
      <ResultOverlay onClick={() => setResult(null)}>
        <ResultCard onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, color: '#0f172a' }}>🎉 Kết quả luyện tập</h2>
            <button
              onClick={() => setResult(null)}
              style={{ border: 'none', background: '#e2e8f0', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontWeight: 700 }}
            >Đóng</button>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: '#0f172a' }}>Đúng: {result.correct}/{result.total} ({result.percentage}%)</span>
          </div>

          <div>
            {perQuestions.map((pq: any, idx: number) => {
              const question = getQuestionById(pq.question_id);
              let content: any = question?.content || {};
              if (typeof content === 'string') {
                try { content = JSON.parse(content); } catch { content = {}; }
              }
              const typeId = question?.question_type_id;
              const userAnswerEntry = submittedAnswers.find((a: any) => Number(a.question_id) === Number(pq.question_id));
              const userAnswerVal = userAnswerEntry ? userAnswerEntry.answer : null;

              // Render question text based on type
              const renderQuestionTextDisplay = () => {
                if (typeId === 3 && content.words) return content.words.join(' / ');
                if (typeId === 4 && content.prompts && content.prompts.length > 0) return content.prompts[0].text;
                if ((typeId === 6 || typeId === 7 || typeId === 9) && content.passage) {
                  const blankRegex = /_{3,}|\.{3,}/g;
                  let blankIndex = 1;
                  return content.passage.replace(blankRegex, () => `(${blankIndex++}) ____`);
                }
                if (typeId === 8 && content.text) return content.text;
                if (typeId === 10 && content.sentence) return content.sentence;
                if (typeId === 11 && content.sentence) {
                  const sentence = content.sentence;
                  const underlined = content.underlined || '';
                  if (underlined && sentence.includes(underlined)) {
                    const parts = sentence.split(underlined);
                    return <>{parts[0]}<u style={{fontWeight:'bold'}}>{underlined}</u>{parts[1]||''}</>;
                  }
                  return sentence;
                }
                if (typeId === 12) {
                  const passageBlock = content.passage ? <div style={{ marginBottom: 6 }}>{content.passage}</div> : null;
                  if (Array.isArray(content.statements) && content.statements.length > 0) {
                    return (
                      <>
                        {passageBlock}
                        <div>{content.statements.map((s: any, idx: number) => `${idx + 1}) ${s?.text || ''}`).join(' | ')}</div>
                      </>
                    );
                  }
                  return content.passage || content.text || '(Không có nội dung)';
                }
                return content.text || content.original_sentence || content.sentence || '(Không có nội dung)';
              };

              // Format user answer based on type
              const formattedAnswer = (() => {
                if (typeId === 10 && Array.isArray(content.options)) {
                  const userIdx = Number(userAnswerVal);
                  if (!Number.isNaN(userIdx) && content.options[userIdx] !== undefined) {
                    return `${String.fromCharCode(65 + userIdx)}. ${content.options[userIdx]}`;
                  }
                }
                if (typeId === 12 && Array.isArray(content.statements)) {
                  const parts = parseTFAnswerParts(userAnswerVal, content.statements.length);
                  return content.statements.map((_: any, idx: number) => {
                    const val = formatTFLabel(parts[idx] || '');
                    return `${idx + 1}) ${val}`;
                  }).join(' | ');
                }
                return userAnswerVal || '(Trống)';
              })();

              // Format correct answer based on type
              const formattedCorrect = (() => {
                // Type 2: Verb Conjugation
                if (typeId === 2) {
                  if (content?.correct_answer) {
                    return content.correct_answer;
                  }
                }
                // Type 3: Sentence Ordering
                if (typeId === 3) {
                  if (content?.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                    return content.correct_answers.join(' | ');
                  }
                  if (content?.correct_sentence) {
                    return content.correct_sentence;
                  }
                }
                // Type 4: Write Sentence with prompts
                if (typeId === 4 && content?.prompts && Array.isArray(content.prompts)) {
                  const allCorrectAnswers: string[] = [];
                  content.prompts.forEach((p: any) => {
                    if (p.answers && Array.isArray(p.answers)) {
                      allCorrectAnswers.push(...p.answers.filter(Boolean));
                    } else if (p.answer) {
                      allCorrectAnswers.push(p.answer);
                    }
                  });
                  if (allCorrectAnswers.length > 0) {
                    return allCorrectAnswers.join(' | ');
                  }
                }
                // Type 5: Rewrite Sentence
                if (typeId === 5) {
                  if (content?.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                    return content.correct_answers.join(' | ');
                  }
                  if (content?.correct_answer) {
                    return content.correct_answer;
                  }
                }
                if (typeId === 10 && Array.isArray(content.options)) {
                  const wrongIdx = content.wrong_index;
                  const correction = content.correct_answer || content.correction || '';
                  if (wrongIdx !== undefined && content.options[wrongIdx] !== undefined) {
                    const label = String.fromCharCode(65 + wrongIdx);
                    return `${label}. ${content.options[wrongIdx]} → ${correction}`;
                  }
                }
                if (typeId === 11) {
                  if (content.correct_questions && Array.isArray(content.correct_questions) && content.correct_questions.length > 0) {
                    return content.correct_questions.join(' | ');
                  }
                  return content.correct_question || question?.correctAnswers?.[0]?.answer || '—';
                }
                if (typeId === 12 && Array.isArray(content.statements)) {
                  return content.statements
                    .map((s: any, idx: number) => `${idx + 1}) ${formatTFLabel(s.is_true ? 'T' : 'F')}`)
                    .join(' | ');
                }
                return question?.correctAnswers?.[0]?.answer || '—';
              })();

              // Calculate type 12 statistics
              const derivedTF = (() => {
                if (typeId !== 12 || !Array.isArray(content.statements)) return null;
                const expected = content.statements.map((s: any) => (s.is_true ? 'T' : 'F'));
                const userParts = parseTFAnswerParts(userAnswerVal, expected.length);
                const correctCount = expected.reduce((acc: number, exp: string, i: number) => acc + (userParts[i] === exp ? 1 : 0), 0);
                const totalSub = expected.length;
                return {
                  expected,
                  userParts,
                  correctCount,
                  totalSub,
                  isAllCorrect: totalSub > 0 && correctCount === totalSub,
                };
              })();

              // Override is_correct for essay-type questions using normalized comparison
              const overrideEssayCorrectness = (() => {
                // Only for essay/text-input types: 2 (VerbConjugation), 3 (SentenceOrdering), 4 (WriteSentence), 5 (RewriteSentence), 6 (ReadingEssay), 11 (MakeQuestion), 13 (WordForm)
                if (![2, 3, 4, 5, 6, 11, 13].includes(typeId)) return null;
                
                const studentAnswer = userAnswerVal;
                if (!studentAnswer || (typeof studentAnswer === 'string' && !studentAnswer.trim())) return false;

                // Type 2: Verb Conjugation - compare with content.correct_answer
                if (typeId === 2) {
                  const correctAnswer = content?.correct_answer || question?.correctAnswers?.[0]?.answer;
                  if (!correctAnswer) return null;
                  return normalizeAnswer(correctAnswer) === normalizeAnswer(studentAnswer);
                }

                // Type 3: compare with correct_answers array or correct_sentence
                if (typeId === 3) {
                  const correctAnswers: string[] = [];
                  if (content?.correct_answers && Array.isArray(content.correct_answers)) {
                    correctAnswers.push(...content.correct_answers);
                  } else if (content?.correct_sentence) {
                    correctAnswers.push(content.correct_sentence);
                  }
                  return correctAnswers.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                }

                // Type 4: compare with multiple answers per prompt
                if (typeId === 4 && content?.prompts && Array.isArray(content.prompts)) {
                  const allCorrectAnswers: string[] = [];
                  content.prompts.forEach((p: any) => {
                    if (p.answers && Array.isArray(p.answers)) {
                      allCorrectAnswers.push(...p.answers.filter(Boolean));
                    } else if (p.answer) {
                      allCorrectAnswers.push(p.answer);
                    }
                  });
                  return allCorrectAnswers.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                }

                // Type 5: compare with correct_answers array or correct_answer
                if (typeId === 5) {
                  const correctAnswers: string[] = [];
                  if (content?.correct_answers && Array.isArray(content.correct_answers)) {
                    correctAnswers.push(...content.correct_answers);
                  } else if (content?.correct_answer) {
                    correctAnswers.push(content.correct_answer);
                  }
                  return correctAnswers.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                }

                // Type 6: Reading Essay - backend handles this
                if (typeId === 6) {
                  return null;
                }

                // Type 11: compare with correct_questions array or correct_question
                if (typeId === 11) {
                  const correctQuestions: string[] = [];
                  if (content?.correct_questions && Array.isArray(content.correct_questions)) {
                    correctQuestions.push(...content.correct_questions);
                  } else if (content?.correct_question) {
                    correctQuestions.push(content.correct_question);
                  } else if (question?.correctAnswers?.[0]?.answer) {
                    correctQuestions.push(question.correctAnswers[0].answer);
                  }
                  return correctQuestions.some(ans => normalizeAnswer(ans) === normalizeAnswer(studentAnswer));
                }

                // Type 13: Word Form - compare with content.correct_answer
                if (typeId === 13) {
                  const correctAnswer = content?.correct_answer || question?.correctAnswers?.[0]?.answer;
                  if (!correctAnswer) return null;
                  return normalizeAnswer(correctAnswer) === normalizeAnswer(studentAnswer);
                }

                return null;
              })();

              const displayIsCorrect = overrideEssayCorrectness !== null 
                ? overrideEssayCorrectness 
                : (derivedTF ? derivedTF.isAllCorrect : pq.is_correct);
              const displayTotalSub = derivedTF ? derivedTF.totalSub : pq.total_sub_questions;
              const displayCorrectCount = derivedTF ? derivedTF.correctCount : pq.correct_count;
              const displayWrongCount = displayTotalSub !== undefined && displayCorrectCount !== undefined
                ? Math.max(displayTotalSub - displayCorrectCount, 0)
                : undefined;

              return (
                <AnswerDetail key={pq.question_id} $isCorrect={displayIsCorrect}>
                  <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 15, color: '#0f172a' }}>
                    {idx + 1}. {typeId === 12 && Array.isArray(content.statements) ? 'True / False' : renderQuestionTextDisplay()}
                    <span style={{ fontStyle: 'italic', fontSize: 12, color: '#999', marginLeft: '6px' }}>
                      [{pq.question_id || question?.id || 'N/A'}]
                    </span>
                  </div>
                  
                  {/* Display passage and statements for type 12 */}
                  {typeId === 12 && content?.passage && (
                    <div style={{ fontSize: 14, color: '#475569', marginBottom: 8, fontStyle: 'italic' }}>
                      {content.passage}
                    </div>
                  )}
                  {typeId === 12 && Array.isArray(content.statements) && (
                    <div style={{ fontSize: 14, color: '#475569', marginBottom: 8 }}>
                      {content.statements.map((stmt: any, i: number) => (
                        <div key={i}>{i + 1}) {stmt.text}</div>
                      ))}
                    </div>
                  )}

                  <div style={{ fontSize: 14, color: '#475569', marginBottom: 4 }}>
                    <strong>Câu trả lời:</strong> {(() => {
                      if (typeId === 12 && Array.isArray(content.statements)) {
                        const parts = parseTFAnswerParts(userAnswerVal, content.statements.length);
                        return (
                          <span>
                            {content.statements.map((_: any, i: number) => {
                              const correctVal = content.statements[i]?.is_true ? 'T' : 'F';
                              const isCorrect = parts[i] === correctVal;
                              const displayAns = parts[i] === 'T' ? 'True' : parts[i] === 'F' ? 'False' : parts[i];
                              return (
                                <span key={i}>
                                  {i > 0 && '  -  '}
                                  <span style={{ color: isCorrect ? '#65a30d' : '#dc2626', fontWeight: 600 }}>
                                    {i + 1}.{displayAns}
                                  </span>
                                </span>
                              );
                            })}
                          </span>
                        );
                      }
                      return formattedAnswer;
                    })()}
                  </div>

                  <div style={{ fontSize: 14, color: '#475569', marginBottom: 8 }}>
                    <strong>Đáp án đúng:</strong> {(() => {
                      if (typeId === 12 && Array.isArray(content.statements)) {
                        return (
                          <span>
                            {content.statements.map((stmt: any, i: number) => {
                              const correctVal = stmt.is_true ? 'True' : 'False';
                              return (
                                <span key={i}>
                                  {i > 0 && '  -  '}
                                  {i + 1}.{correctVal}
                                </span>
                              );
                            })}
                          </span>
                        );
                      }
                      return formattedCorrect;
                    })()}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 700,
                      background: displayIsCorrect ? '#d1fae5' : '#fee2e2',
                      color: displayIsCorrect ? '#065f46' : '#991b1b'
                    }}>
                      {displayIsCorrect ? '✓ Đúng' : '✗ Sai'}
                    </span>
                  </div>

                  {/* Show sub-question stats for types with multiple parts */}
                  {displayTotalSub && displayTotalSub > 1 && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: 13 }}>
                      {displayCorrectCount !== undefined && displayCorrectCount > 0 && (
                        <span style={{ padding: '4px 10px', background: '#d1fae5', color: '#065f46', borderRadius: 6 }}>
                          ✓ Đúng({displayCorrectCount})
                        </span>
                      )}
                      {displayWrongCount !== undefined && displayWrongCount > 0 && (
                        <span style={{ padding: '4px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 6 }}>
                          ✗ Sai({displayWrongCount})
                        </span>
                      )}
                    </div>
                  )}
                </AnswerDetail>
              );
            })}
          </div>

          <div style={{ textAlign: 'right', marginTop: 12 }}>
            <button
              onClick={() => setResult(null)}
              style={{ padding: '12px 18px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}
            >Đóng</button>
          </div>
        </ResultCard>
      </ResultOverlay>
    );
  };

  return (
    <PageContainer>
      {renderResultModal()}
      <PageContent>
        <ExamCard>
          <CardHeader>
            <BackButtonInCard onClick={() => navigate('/student')}>← Quay lại</BackButtonInCard>
            <h2>Luyện tập câu sai</h2>
          </CardHeader>

          <ExamInfo>
            <p>📊 Số câu luyện tập: {exam.exam_questions.length}</p>
          </ExamInfo>

          {groupedByType.map((group, exerciseIdx) => {
            const typeInfo = group.typeInfo || {};
            const headerText = `Exercise ${exerciseIdx + 1}: ${typeInfo.eng_title || typeInfo.vn_title || `Type ${group.typeId}`}${typeInfo.vn_title && typeInfo.eng_title ? ` (${typeInfo.vn_title})` : ''}`;
            
            return (
              <div key={`exercise-${group.typeId}`}>
                <ExerciseHeader>{headerText}</ExerciseHeader>
                
                {group.questions.map((eq: any) => {
                  const question = eq.question;
                  if (!question) return null;
                  
                  const qid = question.id;
                  const globalIndex = questionNumberMap[qid] || 0;
                  
                  return (
                    <QuestionContainer key={eq.id}>
                      <QuestionText>
                        {globalIndex}. {question.question_type_id === 12 ? 'True / False' : renderQuestionText(question)} <span style={{fontStyle: 'italic', color: '#666', fontSize: '13px'}}>({qid})</span>
                      </QuestionText>
                      {renderQuestionBody(question)}
                    </QuestionContainer>
                  );
                })}
              </div>
            );
          })}

          <SubmitButton
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Đang nộp...' : '✅ Nộp bài'}
          </SubmitButton>
        </ExamCard>
      </PageContent>
    </PageContainer>
  );
};

export default RetryExamPage;
